/**
 * The API client. One function per route in `apps/web/src/server.ts`.
 *
 * Two rules hold this file together, and both exist because of what the server
 * is doing on the other end.
 *
 * CREDENTIALS ON EVERY CALL. The session is an httpOnly cookie, so `fetch`
 * omits it unless asked. A missing `credentials: 'include'` does not fail
 * loudly — it produces a 401 that looks exactly like an expired session, on one
 * route, for one person. `request()` is the only place a fetch happens so the
 * flag cannot be forgotten on a new endpoint.
 *
 * SERVER MESSAGES SURVIVE VERBATIM. The API writes its 4xx bodies for an
 * operator to read: the double-file guard explains why filing twice jeopardises
 * both claims, and a refused transition names what is missing or what is
 * actually available instead. Those sentences are the safety rail. Replacing
 * them with "Request failed" removes the only explanation the operator gets,
 * so `ApiError.message` is the server's `{error}` string unaltered, and a
 * generic message is used only when the server sent nothing to use.
 */

/** Roles, mirroring `UserRole` in `apps/web/src/auth.ts`. */
export type UserRole = 'ops' | 'seller';

export interface AuthenticatedUser {
  readonly id: string;
  readonly email: string;
  readonly role: UserRole;
  /** Null for ops users. Sellers are scoped to this in the query layer. */
  readonly sellerId: string | null;
}

export type ClaimCode =
  'CONSUMER' | 'IBR' | 'INVENTORY_DISCREPANCY' | 'COMMISSION_CLAWBACK';

export type ClaimStatus =
  | 'detected'
  | 'reviewed'
  | 'evidence_pending'
  | 'ready_to_file'
  | 'filed'
  | 'pending_platform'
  | 'paid'
  | 'rejected'
  | 'refiled'
  | 'abandoned'
  | 'expired';

export type Classification = 'claimable' | 'noise' | 'needs_review';

/** What a transition asks the operator for before it can be recorded honestly. */
export type TransitionRequirement = 'ticketId' | 'rejectionReason' | 'paidAmount' | 'reason';

export interface AvailableTransition {
  readonly event: string;
  /** Button text. */
  readonly label: string;
  /** What the operator is asserting by taking this action. Shown as helper text. */
  readonly meaning: string;
  readonly requires: readonly TransitionRequirement[];
}

export interface QueueFinding {
  readonly id: string;
  readonly fingerprint: string;
  readonly shopId: string;
  readonly skuId: string | null;
  readonly claimCode: ClaimCode;
  readonly claimLabel: string;
  readonly quantity: number;
  /** Server-formatted money. Null when the SKU has no cost basis. */
  readonly value: string | null;
  readonly valueMinor: number | null;
  readonly clockTrigger: string;
  readonly clockStartDate: string;
  readonly expiresAt: string | null;
  /** Null for the settlement classes, which TikTok states no deadline for. */
  readonly daysLeft: number | null;
  readonly detail: string;
}

export interface QueueResponse {
  readonly asOf: string;
  readonly findings: readonly QueueFinding[];
}

export interface OpsClaim {
  readonly id: string;
  readonly status: ClaimStatus;
  readonly ticketId: string | null;
  readonly refileCount: number;
  readonly claimCode: ClaimCode;
  readonly expiresAt: string | null;
  readonly daysLeft: number | null;
  readonly valueMinor: number | null;
  readonly available: readonly AvailableTransition[];
}

export interface ClaimEventRow {
  readonly id: string;
  readonly claimId: string;
  readonly eventType: string;
  readonly fromStatus: ClaimStatus | null;
  readonly toStatus: ClaimStatus | null;
  readonly actorType: 'ops_user' | 'seller' | 'system';
  readonly actorUserId: string | null;
  readonly actorLabel: string | null;
  readonly note: string | null;
  readonly payload: unknown;
  readonly occurredAt: string;
}

export interface SellerFinding {
  readonly shopName: string | null;
  readonly claimCode: ClaimCode;
  readonly claimLabel: string;
  readonly quantity: number;
  readonly valueMinor: number | null;
  readonly status: Classification;
  readonly expiresAt: string | null;
}

export interface SellerFindingsResponse {
  readonly asOf: string;
  readonly findings: readonly SellerFinding[];
}

export interface TransitionInput {
  readonly event: string;
  readonly ticketId?: string;
  readonly rejectionReason?: string;
  /** Minor units. Never a float — see `parseMinor` in `format.ts`. */
  readonly paidAmountMinor?: number;
  readonly reason?: string;
}

export interface TransitionResult {
  readonly claimId: string;
  readonly from: ClaimStatus;
  readonly to: ClaimStatus;
}

/**
 * A failed API call, carrying the server's own words.
 *
 * `status` matters as much as the message, because three codes mean three
 * different things to an operator: 409 is the double-file guard refusing a
 * dangerous action, 422 is a transition the lifecycle does not allow from
 * here, and 401 means the session is gone. The UI branches on the number and
 * shows the string.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The double-file guard, or a database integrity guard. A safety rail, not a validation error. */
  get isGuard(): boolean {
    return this.status === 409;
  }

  /** The lifecycle refused the transition, or required data was missing. */
  get isRefusedTransition(): boolean {
    return this.status === 422;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/**
 * Pull the operator-facing message out of a failed response.
 *
 * The server's error handler always sends `{ error }`, including for 5xx —
 * where the string is deliberately generic, since an internal message can name
 * a table or a constraint. Anything that is not that shape (a proxy error page,
 * a dropped connection mid-body) has no message worth showing, so the status
 * line is the honest fallback.
 */
async function errorFrom(response: Response): Promise<ApiError> {
  let message = `Request failed (HTTP ${response.status}).`;
  try {
    const body: unknown = await response.json();
    const serverMessage = (body as { error?: unknown } | null)?.error;
    if (typeof serverMessage === 'string' && serverMessage.length > 0) {
      message = serverMessage;
    }
  } catch {
    // Body was not JSON. The status-line fallback above stands.
  }
  return new ApiError(response.status, message);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // The session cookie is the entire authentication factor. See the file
    // header — this is why every call goes through here.
    credentials: 'include',
    headers:
      init.body === undefined
        ? init.headers
        : { 'content-type': 'application/json', ...init.headers },
  });

  if (!response.ok) throw await errorFrom(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

/**
 * Ask for a magic link.
 *
 * The server answers `{ sent: true }` whether or not the address belongs to a
 * user — deliberate, so this endpoint cannot be driven as an account-enumeration
 * oracle. Callers must therefore never phrase the result as "we found you" or
 * "we sent it to your account". See `LoginPage` for the wording that keeps that
 * property intact in the UI.
 */
export function requestMagicLink(email: string): Promise<{ sent: boolean }> {
  return post<{ sent: boolean }>('/auth/request-link', { email });
}

export function logout(): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>('/auth/logout');
}

/** Who is signed in, or `{ user: null }`. This is what routes the app by role. */
export function getMe(): Promise<{ user: AuthenticatedUser | null }> {
  return request<{ user: AuthenticatedUser | null }>('/api/me');
}

// ---------------------------------------------------------------------------
// Ops
// ---------------------------------------------------------------------------

export interface QueueParams {
  readonly shopId?: string;
  readonly withinDays?: number;
  readonly limit?: number;
}

export function getQueue(params: QueueParams = {}): Promise<QueueResponse> {
  const search = new URLSearchParams();
  if (params.shopId) search.set('shopId', params.shopId);
  if (params.withinDays !== undefined) search.set('withinDays', String(params.withinDays));
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  const query = search.toString();
  return request<QueueResponse>(`/api/ops/queue${query ? `?${query}` : ''}`);
}

export function getClaims(shopId?: string): Promise<{ claims: readonly OpsClaim[] }> {
  const query = shopId ? `?shopId=${encodeURIComponent(shopId)}` : '';
  return request<{ claims: readonly OpsClaim[] }>(`/api/ops/claims${query}`);
}

/**
 * One claim, filtered out of the active list.
 *
 * There is no `GET /api/ops/claims/:id`, so this is the list endpoint plus a
 * find. The consequence is real and worth knowing: the list returns only
 * claims still occupying their finding's active slot, so a claim that has
 * reached `paid`, `abandoned` or `expired` cannot be loaded at all. Reaching a
 * terminal state currently makes the claim's own page unreachable, which is
 * also where its audit trail is read from.
 */
export async function getClaim(claimId: string): Promise<OpsClaim | null> {
  const { claims } = await getClaims();
  return claims.find((claim) => claim.id === claimId) ?? null;
}

/**
 * Open a claim against a finding.
 *
 * Throws `ApiError` with status 409 when the finding already has an active
 * claim, or has been marked as filed by another party. Both are the double-file
 * guard, and the message explains why a second filing jeopardises both claims.
 * Surface it; do not retry it.
 */
export function openClaim(findingId: string): Promise<{ id: string; status: ClaimStatus }> {
  return post<{ id: string; status: ClaimStatus }>('/api/ops/claims', { findingId });
}

/**
 * Move a claim through the lifecycle.
 *
 * Throws `ApiError` with status 422 when the state machine refuses — the
 * message names either what is missing or which events are available instead,
 * so it goes on screen unchanged.
 */
export function transitionClaim(
  claimId: string,
  input: TransitionInput,
): Promise<TransitionResult> {
  return post<TransitionResult>(`/api/ops/claims/${encodeURIComponent(claimId)}/transition`, input);
}

/** The audit trail, oldest first. Append-only on the server. */
export function getClaimHistory(claimId: string): Promise<{ events: readonly ClaimEventRow[] }> {
  return request<{ events: readonly ClaimEventRow[] }>(
    `/api/ops/claims/${encodeURIComponent(claimId)}/history`,
  );
}

/**
 * Record that a finding was already filed by the seller or another party.
 *
 * The note is required by the server, and it is the only record of who filed
 * and where. This bars a claim afterwards, so it is not reversible from the UI.
 */
export function markExternallyFiled(findingId: string, note: string): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>(
    `/api/ops/findings/${encodeURIComponent(findingId)}/externally-filed`,
    { note },
  );
}

// ---------------------------------------------------------------------------
// Seller
// ---------------------------------------------------------------------------

/** Scoped server-side to the signed-in seller. No shop parameter can widen it. */
export function getSellerFindings(): Promise<SellerFindingsResponse> {
  return request<SellerFindingsResponse>('/api/seller/findings');
}
